import { z } from 'zod';

/**
 * Runtime validation of required Vite environment variables.
 *
 * Vite exposes `VITE_*` variables at build time via `import.meta.env`.
 * We validate once on import so a missing or malformed variable surfaces
 * as a clear error during the initial render rather than a cryptic
 * runtime crash deep inside a data hook.
 */
const EnvSchema = z.object({
  VITE_API_URL: z.string().url().min(1),
  VITE_PUBLIC_URL: z.string().url().optional().default('http://localhost:5173'),
  VITE_SENTRY_DSN: z.string().optional().default(''),
  VITE_DEFAULT_LOCALE: z.enum(['fr', 'en', 'es']).optional().default('fr'),
});

export type Env = z.infer<typeof EnvSchema>;

function parseEnv(): Env {
  const raw = {
    VITE_API_URL: import.meta.env.VITE_API_URL as string | undefined,
    VITE_PUBLIC_URL: import.meta.env.VITE_PUBLIC_URL as string | undefined,
    VITE_SENTRY_DSN: import.meta.env.VITE_SENTRY_DSN as string | undefined,
    VITE_DEFAULT_LOCALE: import.meta.env.VITE_DEFAULT_LOCALE as string | undefined,
  };
  const parsed = EnvSchema.safeParse(raw);
  if (!parsed.success) {
    // eslint-disable-next-line no-console
    console.error('[env] invalid configuration:', parsed.error.flatten());
    throw new Error(
      'Missing or invalid environment variables. Check .env against .env.example.',
    );
  }
  return parsed.data;
}

export const env: Env = parseEnv();
